import { queryOptions } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import type { Insert, Tables, Update } from "@/types/database";
import { qk } from "./_queryKeys";
import { assertData, throwIfError } from "./_supabaseErrors";
import { excludeTestData } from "./testData";
import { insertWithWriteSource, type WriteSource } from "./writeSource";
import { indexContactRow } from "./knowledge";

export type Contact = Tables<"contacts">;
export type ContactInsert = Insert<"contacts">;
export type ContactUpdate = Update<"contacts">;
export type ContactWithTaskCount = Contact & { taskCount: number; quoteCount: number };

export async function listContacts(): Promise<Contact[]> {
  const { data, error } = await supabase
    .from("contacts")
    .select("*")
    .order("created_at", { ascending: false });
  throwIfError(error);
  return excludeTestData(data);
}

export async function getContact(id: string): Promise<Contact> {
  const { data, error } = await supabase.from("contacts").select("*").eq("id", id).single();
  throwIfError(error);
  return assertData(data, "Contact not found");
}

function scheduleContactIndex(row: Contact): void {
  void indexContactRow(row).catch(() => {
    // Indexelés best-effort — a mentés már sikerült.
  });
}

export async function createContact(values: ContactInsert, source: WriteSource = "ui"): Promise<Contact> {
  const row = await insertWithWriteSource<Contact>(
    "contacts",
    values as unknown as Record<string, unknown>,
    source
  );
  scheduleContactIndex(row);
  return row;
}

export async function updateContact(id: string, values: ContactUpdate): Promise<Contact> {
  const { data, error } = await supabase
    .from("contacts")
    .update(values)
    .eq("id", id)
    .select("*")
    .single();
  throwIfError(error);
  const row = assertData(data, "Contact not found");
  scheduleContactIndex(row);
  return row;
}

export async function deleteContact(id: string): Promise<void> {
  const { error } = await supabase.from("contacts").delete().eq("id", id);
  throwIfError(error);
}

export async function listContactsWithTaskCount(): Promise<ContactWithTaskCount[]> {
  const contacts = await listContacts();
  if (contacts.length === 0) return [];

  const [{ data: tasks, error: tErr }, { data: quotes, error: qErr }] = await Promise.all([
    supabase.from("tasks").select("assigned_contact_id"),
    supabase.from("quotes").select("contact_id")
  ]);
  throwIfError(tErr);
  throwIfError(qErr);

  const taskCounts = new Map<string, number>();
  for (const t of tasks ?? []) {
    if (!t.assigned_contact_id) continue;
    taskCounts.set(t.assigned_contact_id, (taskCounts.get(t.assigned_contact_id) ?? 0) + 1);
  }

  const quoteCounts = new Map<string, number>();
  for (const q of quotes ?? []) {
    if (!q.contact_id) continue;
    quoteCounts.set(q.contact_id, (quoteCounts.get(q.contact_id) ?? 0) + 1);
  }

  return contacts.map((c) => ({
    ...c,
    taskCount: taskCounts.get(c.id) ?? 0,
    quoteCount: quoteCounts.get(c.id) ?? 0
  }));
}

export const contactsQueryOptions = () =>
  queryOptions({
    queryKey: qk.contacts,
    queryFn: listContacts
  });

export const contactsWithTaskCountQueryOptions = () =>
  queryOptions({
    queryKey: ["contacts", "withTaskCount"] as const,
    queryFn: listContactsWithTaskCount
  });

export const contactQueryOptions = (id: string) =>
  queryOptions({
    queryKey: qk.contact(id),
    queryFn: () => getContact(id)
  });

